<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

type NavItem = {
  name: string
  path: string
  icon: string
  description: string
  external?: boolean
}

const isSidebarOpen = ref(true)
const showUserMenu = ref(false)

// Close menu when clicking outside
function handleClickOutside(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (!target.closest('.user-section')) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Simplified navigation - focused sections
const navigation = computed(() => {
  const items: NavItem[] = [
    { name: 'Dashboard', path: '/', icon: 'dashboard', description: 'Overview & quick actions' },
    { name: 'Instances', path: '/instances', icon: 'instances', description: 'EMR tenant management' },
    { name: 'Consumers', path: '/consumers', icon: 'consumers', description: 'Multi-consumer management' },
    { name: 'Internal Apps', path: '/internal-apps', icon: 'consumers', description: 'CRM and Team Recovery keys' },
    { name: 'MCP Consumers', path: '/mcp-consumers', icon: 'consumers', description: 'atlas-mcp gateway credentials' },
    { name: 'Operations', path: '/operations', icon: 'operations', description: 'Activity log' },
    { name: 'Requests', path: '/stats', icon: 'stats', description: 'Requests log' },
    { name: 'Jobs', path: '/jobs', icon: 'jobs', description: 'Scheduled tasks' },
  ]
  
  // Admin / Support can view Users (write gated in the view)
  if (authStore.canReadUsers) {
    items.push({ name: 'Users', path: '/users', icon: 'users', description: 'Team management' })
  }
  
  items.push(
    { name: 'API Docs', path: '/docs/', icon: 'help', description: 'OpenAPI reference & Try it out', external: true },
    { name: 'Settings', path: '/settings', icon: 'settings', description: 'Gateway validation' },
  )
  
  return items
})

function isActive(path: string, external?: boolean) {
  if (external || path.startsWith('/docs')) {
    return route.path === '/docs' || route.path.startsWith('/docs/')
  }
  return route.path === path
}

async function logout() {
  await authStore.logout()
  router.push('/login')
}
</script>

<template>
  <div class="app">
    <!-- Header with purple gradient -->
    <header class="app-header">
      <div class="header-left">
        <RouterLink to="/" class="app-logo">
          <div class="logo-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <span v-if="isSidebarOpen">Kipu <span class="logo-accent">API</span></span>
        </RouterLink>
      </div>
      
      <div class="header-right">
        <div v-if="authStore.user" class="user-section" @click="showUserMenu = !showUserMenu">
          <span class="user-name">{{ authStore.user?.name || 'User' }}</span>
          <div class="user-avatar">
            <span>{{ authStore.user?.name?.charAt(0) || 'U' }}</span>
          </div>
          <svg class="dropdown-icon" :class="{ open: showUserMenu }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
          
          <!-- User dropdown menu -->
          <div v-if="showUserMenu" class="user-dropdown">
            <div class="dropdown-header">
              <div class="dropdown-user-name">{{ authStore.user?.name }}</div>
              <div class="dropdown-user-email">{{ authStore.user?.email }}</div>
              <div class="dropdown-user-role">{{ authStore.user?.role }}</div>
            </div>
            <div class="dropdown-divider"></div>
            <button class="dropdown-item logout-item" @click.stop="logout">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </header>

    <div class="app-body">
      <!-- Sidebar -->
      <aside :class="['sidebar', { collapsed: !isSidebarOpen }]">
        <nav class="sidebar-nav">
          <template v-for="item in navigation" :key="item.path">
          <a
            v-if="item.external"
            :href="item.path"
            :class="['nav-item', { active: isActive(item.path, true) }]"
          >
            <span class="nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                <line x1="8" y1="6" x2="16" y2="6"/>
                <line x1="8" y1="10" x2="16" y2="10"/>
                <line x1="8" y1="14" x2="12" y2="14"/>
              </svg>
            </span>
            <span v-if="isSidebarOpen" class="nav-label">{{ item.name }}</span>
          </a>
          <RouterLink
            v-else
            :to="item.path"
            :class="['nav-item', { active: isActive(item.path) }]"
          >
            <span class="nav-icon">
              <!-- Dashboard -->
              <svg v-if="item.icon === 'dashboard'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              
              <!-- Instances (server) -->
              <svg v-else-if="item.icon === 'instances'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                <line x1="6" y1="6" x2="6.01" y2="6"/>
                <line x1="6" y1="18" x2="6.01" y2="18"/>
              </svg>
              
              <!-- Credentials (key) -->
              <svg v-else-if="item.icon === 'credentials'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
              </svg>
              
              <!-- Consumers (multiple users) -->
              <svg v-else-if="item.icon === 'consumers'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              
              <!-- Operations (activity/pulse) -->
              <svg v-else-if="item.icon === 'operations'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              
              <!-- Jobs (clock/schedule) -->
              <svg v-else-if="item.icon === 'jobs'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              
              <!-- Stats (chart/analytics) -->
              <svg v-else-if="item.icon === 'stats'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
              
              <!-- Help/Docs (book/document) -->
              <svg v-else-if="item.icon === 'help'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                <line x1="8" y1="6" x2="16" y2="6"/>
                <line x1="8" y1="10" x2="16" y2="10"/>
                <line x1="8" y1="14" x2="12" y2="14"/>
              </svg>
              
              <!-- Settings (gear) -->
              <svg v-else-if="item.icon === 'settings'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              
              <!-- Reconciliation (sync arrows) -->
              <svg v-else-if="item.icon === 'reconciliation'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M23 4v6h-6M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              
              <!-- Users (people) -->
              <svg v-else-if="item.icon === 'users'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
            <span v-if="isSidebarOpen" class="nav-label">{{ item.name }}</span>
          </RouterLink>
          </template>
        </nav>

        <!-- Sidebar footer -->
        <div v-if="isSidebarOpen" class="sidebar-footer">
          <div class="user-info">
            <div class="user-info-avatar">{{ authStore.user?.name?.charAt(0) || 'U' }}</div>
            <div class="user-info-details">
              <span class="user-info-name">{{ authStore.user?.name || 'User' }}</span>
              <span class="user-info-role">{{ authStore.user?.role || 'Role' }}</span>
            </div>
          </div>
        </div>

        <!-- Toggle button -->
        <button @click="isSidebarOpen = !isSidebarOpen" class="sidebar-toggle">
          <svg :class="['toggle-icon', { rotated: !isSidebarOpen }]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </aside>

      <!-- Main content -->
      <main class="main-content">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
}

.app-header {
  background: linear-gradient(135deg, #5B36B1 0%, #7C52D9 100%);
  color: white;
  padding: 0 24px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 12px rgba(91, 54, 177, 0.25);
}

.header-left {
  display: flex;
  align-items: center;
}

.app-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: white;
  text-decoration: none;
  letter-spacing: -0.02em;
}

.logo-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 4px;
}

.logo-icon svg {
  width: 100%;
  height: 100%;
}

.logo-accent {
  opacity: 0.85;
  font-weight: 400;
}

.header-right {
  display: flex;
  align-items: center;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.user-section:hover {
  background: rgba(255, 255, 255, 0.18);
}

.user-name {
  font-size: 13px;
  font-weight: 500;
  opacity: 0.95;
}

.user-avatar {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
}

.dropdown-icon {
  width: 16px;
  height: 16px;
  opacity: 0.7;
  transition: transform 0.2s ease;
}

.dropdown-icon.open {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  min-width: 220px;
  z-index: 1000;
  overflow: hidden;
}

.dropdown-header {
  padding: 16px;
  background: var(--bg-secondary);
}

.dropdown-user-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.dropdown-user-email {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.dropdown-user-role {
  font-size: 10px;
  color: var(--primary);
  background: var(--primary-bg);
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.dropdown-divider {
  height: 1px;
  background: var(--border-subtle);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.dropdown-item:hover {
  background: var(--bg-secondary);
}

.dropdown-item svg {
  width: 18px;
  height: 18px;
  color: var(--text-secondary);
}

.logout-item {
  color: #dc2626;
}

.logout-item svg {
  color: #dc2626;
}

.logout-item:hover {
  background: rgba(220, 38, 38, 0.08);
}

.app-body {
  display: flex;
  flex: 1;
}

.sidebar {
  width: 240px;
  background: var(--bg-secondary);
  border-right: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  position: relative;
  transition: width 0.2s ease;
}

.sidebar.collapsed {
  width: 64px;
}

.sidebar-nav {
  flex: 1;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  border-radius: var(--radius-md);
  transition: all 0.15s ease;
}

.nav-item:hover {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.nav-item.active {
  color: var(--primary);
  background: var(--primary-bg);
}

.nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-icon svg {
  width: 20px;
  height: 20px;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid var(--border-subtle);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info-avatar {
  width: 36px;
  height: 36px;
  background: var(--primary-bg);
  color: var(--primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
}

.user-info-details {
  display: flex;
  flex-direction: column;
}

.user-info-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.user-info-role {
  font-size: 11px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.sidebar-toggle {
  position: absolute;
  right: -12px;
  top: 24px;
  width: 24px;
  height: 24px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-subtle);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: all 0.15s ease;
}

.sidebar-toggle:hover {
  background: var(--bg-tertiary);
}

.toggle-icon {
  width: 14px;
  height: 14px;
  color: var(--text-muted);
  transition: transform 0.2s ease;
}

.toggle-icon.rotated {
  transform: rotate(180deg);
}

.main-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}
</style>
